import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Lock, ArrowRight, ArrowLeft } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import { useAuth } from '../../../contexts/AuthContext'
import { CaseTransitionModal } from '../InvestigationPipeline'
import {
  TRANSITION_RULES,
  STAGE_TO_STATUS,
  STAGES,
  ruleChangesApiStatus,
  buildWorkflowCaseFromApi,
  canUserTransitionCase,
  hasPipelineManagePermission,
  hasPipelineViewPermission,
  pickStageEnteredAt,
  getStageStaleInfo,
  formatDaysInStageLabel,
  stageAgingTooltip,
} from '../investigationPipelineConfig'

const LEGACY_ASSIGNEE_ID_TO_NAME = {
  SC: 'Sarah Chen',
  JP: 'James Park',
  MW: 'Marcus Webb',
  LT: 'Linda Tran',
  AB: 'Anna Burgess',
}

function remapRoutingAssignees(routing, members) {
  if (!routing || typeof routing !== 'object') return {}
  const byName = new Map(members.map((m) => [String(m.name || '').toLowerCase().trim(), m]))
  const resolveId = (rawId) => {
    if (rawId == null || rawId === '') return null
    const sid = String(rawId)
    if (members.some((m) => String(m.id) === sid)) return sid
    const legacyName = LEGACY_ASSIGNEE_ID_TO_NAME[sid]
    if (legacyName) {
      const m = byName.get(legacyName.toLowerCase())
      return m ? String(m.id) : null
    }
    return null
  }
  const out = {}
  for (const [k, v] of Object.entries(routing)) {
    if (!v || typeof v !== 'object') continue
    out[k] = {
      ...v,
      defaultAssignee: resolveId(v.defaultAssignee),
      observers: Array.isArray(v.observers) ? [...new Set(v.observers.map(resolveId).filter(Boolean))] : [],
    }
  }
  return out
}

/**
 * Compact pipeline stage transitions on the investigation case page (same rules/API as Investigation Pipeline).
 */
export default function CaseWorkflowTransitions({ caseData, teamMembers = [], stageLabels = {}, onTransitionComplete }) {
  const { user: authUser, can } = useAuth()
  const [routing, setRouting] = useState({})
  const [pendingRule, setPendingRule] = useState(null)
  const [error, setError] = useState(null)
  const transitionInFlightRef = useRef(false)

  const canViewPipeline = hasPipelineViewPermission(can)
  const canEditCases = can('compliance-safe/cases', 'edit')
  const canManagePipeline = hasPipelineManagePermission(can)

  const workflowCase = caseData ? buildWorkflowCaseFromApi(caseData, teamMembers) : null
  const canTransition = workflowCase
    ? canUserTransitionCase(workflowCase, teamMembers, authUser, canEditCases, canManagePipeline)
    : false

  const rawRules = workflowCase ? (TRANSITION_RULES[workflowCase.stage] || { forward: [], backward: [] }) : { forward: [], backward: [] }
  const apiSt = workflowCase?._apiStatus
  const forwardRules = (rawRules.forward || []).filter((r) => ruleChangesApiStatus(r, apiSt))
  const backwardRules = (rawRules.backward || []).filter((r) => ruleChangesApiStatus(r, apiSt))
  const actionRules = [...forwardRules, ...backwardRules]
  const terminal = workflowCase ? ['filed', 'closed'].includes(workflowCase.stage) : false

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/cases/pipeline/routing'))
        const json = await res.json()
        if (!cancelled && json.success && json.data) {
          setRouting(remapRoutingAssignees(json.data, teamMembers))
        }
      } catch (_) { /* optional */ }
    })()
    return () => { cancelled = true }
  }, [teamMembers])

  const confirmTransition = useCallback(async (payload) => {
    if (transitionInFlightRef.current || !pendingRule || !workflowCase) return
    const rule = pendingRule
    const newStatus = STAGE_TO_STATUS[rule.to]
    if (!newStatus) {
      setPendingRule(null)
      setError('Transition not supported for this stage')
      return
    }
    transitionInFlightRef.current = true
    setError(null)
    try {
      const body = {
        status: newStatus,
        reason: payload.reason || `Moved to ${rule.to}`,
        assignee: payload.assignee || '',
        observers: Array.isArray(payload.observers) ? payload.observers : [],
      }
      if (payload.filing && rule.to === 'filed') body.filing = payload.filing
      if (payload.suspicion_basis && rule.to === 'suspicion_formed') body.suspicion_basis = payload.suspicion_basis
      if (payload.resolution_status && rule.to === 'closed') body.resolution_status = payload.resolution_status

      const res = await authFetch(
        getLocalApiUrl(`/api/cases/${encodeURIComponent(workflowCase._dbId)}/status`),
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      )
      const raw = await res.text()
      let json = {}
      try { json = raw ? JSON.parse(raw) : {} } catch (_) {
        throw new Error(raw?.slice(0, 200) || `Update failed (${res.status})`)
      }
      if (!res.ok || !json.success) {
        throw new Error(json.error || json.message || `Update failed (${res.status})`)
      }
      setPendingRule(null)
      if (onTransitionComplete) await onTransitionComplete()
    } catch (e) {
      setError(e.message || 'Transition failed')
    } finally {
      transitionInFlightRef.current = false
    }
  }, [pendingRule, workflowCase, onTransitionComplete])

  if (!canViewPipeline || !caseData || !workflowCase) return null

  const stageStale = getStageStaleInfo(
    pickStageEnteredAt(caseData, workflowCase.stage, caseData.status),
    workflowCase.stage
  )

  const renderActionOptions = () => {
    if (!canEditCases) {
      return <span className="text-slate-500">Investigation edit permission required</span>
    }
    if (terminal) {
      return <span className="text-slate-500">Terminal stage — no further moves</span>
    }
    if (!canTransition && workflowCase.assignee?.userId) {
      return (
        <span className="text-amber-800">
          Only {workflowCase.assignee.name} or a pipeline manager can transition
        </span>
      )
    }
    if (actionRules.length === 0) {
      return <span className="text-slate-500">No transitions available</span>
    }
    return actionRules.map((rule) => {
      const isBackward = backwardRules.includes(rule)
      return (
        <button
          key={`${isBackward ? 'b' : 'f'}-${rule.to}`}
          type="button"
          disabled={!canTransition}
          onClick={() => canTransition && setPendingRule({ ...rule, direction: isBackward ? 'backward' : 'forward' })}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold border shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
            isBackward
              ? 'border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100'
              : 'border-psg-teal/30 bg-psg-teal/5 text-psg-teal hover:bg-psg-teal/10'
          }`}
        >
          {isBackward ? <ArrowLeft size={11} /> : <ArrowRight size={11} />}
          {rule.label}
        </button>
      )
    })
  }

  return (
    <>
      <div
        className={`rounded-lg border px-4 py-2 mx-auto ${stageStale ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}
        style={{ maxWidth: 1400 }}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 min-w-0">
            {stageStale && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-md font-bold text-amber-800 bg-amber-100 border border-amber-300 shrink-0"
                title={stageAgingTooltip(workflowCase.stageLabel, pickStageEnteredAt(caseData, workflowCase.stage, caseData.status))}
              >
                {formatDaysInStageLabel(stageStale.daysInStage, workflowCase.stageLabel, { context: 'detail' })}
              </span>
            )}
            <span className="font-semibold text-slate-600 shrink-0">Pipeline action:</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {renderActionOptions()}
            </div>
            {canEditCases && actionRules.length > 0 && !canTransition && (
              <span className="inline-flex items-center gap-1 text-slate-400 shrink-0">
                <Lock size={11} />
              </span>
            )}
          </div>
          <Link
            to="/compliance-safe/investigations/pipeline"
            className="font-semibold text-psg-teal hover:underline shrink-0"
          >
            Open pipeline board
          </Link>
        </div>
        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1.5">{error}</div>
        )}
      </div>

      {pendingRule && (
        <CaseTransitionModal
          caseData={workflowCase}
          rule={pendingRule}
          canTransitionCase={canTransition}
          routing={routing}
          onConfirm={confirmTransition}
          onCancel={() => setPendingRule(null)}
          stages={STAGES.map((s) => ({
            ...s,
            label: stageLabels[s.id] || s.label,
            color: '#0a5169',
            bg: '#e8f2f6',
          }))}
          teamMembers={teamMembers}
        />
      )}
    </>
  )
}
